// =============================================================================
// components/ui/ScreenHeading.tsx — the ONE screen-title treatment.
//
// CORRECT premium implementation (client: "ese negro… de la cagada" — the flat
// BLACK titles): the title is now a real GRADIENT-FILLED headline (brand purple
// #6C5CE7 → #A855F7) via MaskedView, the way Coinbase/Phantom/OpenSea do it — not
// black text, not a cheap solid-color swap. Optional uppercase kicker above.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, StyleProp, ViewStyle, TextStyle } from 'react-native';
import MaskedView from '@react-native-masked-view/masked-view';
import LinearGradient from 'react-native-linear-gradient';
import { colors, fonts, spacing } from '../../constants/theme';

// Gradient-filled text: the mask (the Text) punches the glyph shape out of the
// gradient. An invisible copy of the Text sizes the gradient to the glyph bounds.
function GradientText({ children, style }: { children: string; style: StyleProp<TextStyle> }) {
  return (
    <MaskedView
      maskElement={<Text style={style}>{children}</Text>}
    >
      <LinearGradient colors={[...colors.gradient]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0.6 }}>
        <Text style={[style, styles.hiddenSizer]}>{children}</Text>
      </LinearGradient>
    </MaskedView>
  );
}

export default function ScreenHeading({
  kicker,
  title,
  subtitle,
  size = 'lg',
  style,
}: {
  kicker?: string;
  title: string;
  subtitle?: string;
  /** lg = big page title (Discover/Learn), md = section title. */
  size?: 'lg' | 'md';
  style?: StyleProp<ViewStyle>;
}) {
  const titleSize = size === 'lg' ? 30 : 22;
  const titleLh = size === 'lg' ? 36 : 28;
  const titleStyle: TextStyle = {
    ...fonts.extrabold,
    color: '#000', // color is irrelevant — the gradient masks the glyphs
    fontSize: titleSize,
    lineHeight: titleLh,
    letterSpacing: -0.6,
  };
  return (
    <View style={style}>
      {!!kicker && <Text style={styles.kicker}>{kicker.toUpperCase()}</Text>}
      <GradientText style={titleStyle}>{title}</GradientText>
      {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  kicker: {
    ...fonts.bold,
    fontSize: 11,
    color: colors.primary,
    letterSpacing: 1.5,
    marginBottom: spacing.xs,
  },
  hiddenSizer: { opacity: 0 },
  subtitle: {
    ...fonts.regular,
    fontSize: 14,
    color: colors.muted,
    marginTop: spacing.sm,
    lineHeight: 20,
  },
});
